import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Paginate } from '@shared/entities/paginate';
import { Ticket } from '@shared/entities/ticket';
import { ApiService, Pagination, PER_PAGE } from '@shared/services';
import { BehaviorSubject, Observable, Subject, shareReplay, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][Ticket] ';

export interface TicketFilterURL {
  caseNumber?: string;
  serialNumber?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
}

@Injectable({
  providedIn: 'root'
})
export class TicketService extends ApiService<Ticket> {
  private refreshClosed$: Subject<Pagination>;
  private refreshOpened$: Subject<Pagination>;
  allClosed$: Observable<Paginate<Ticket>>;
  currentClosedPaginator: Pagination;
  allOpened$: Observable<Paginate<Ticket>>;
  currentOpenedPaginator: Pagination;
  currentFiltersClosed: TicketFilterURL = {};

  set filtersClosed(value: TicketFilterURL) {
    this.currentFiltersClosed = value;
    this.refreshClosed(1, PER_PAGE);
  }

  constructor(private activatedRoute: ActivatedRoute) {
    const masterUrl = '/tickets';
    const detailUrl = '/tickets';

    super(Ticket, masterUrl, detailUrl, undefined, 'newTicket');

    this.currentClosedPaginator = { page: 1, perPage: PER_PAGE };
    this.currentOpenedPaginator = { page: 1, perPage: PER_PAGE };

    this.refreshClosed$ = new BehaviorSubject<Pagination>(this.currentClosedPaginator);
    this.allClosed$ = this.refreshClosed$.pipe(
      switchMap((pagination) => {
        const { locationId } = this.activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        this.setUrlParameter('isClosed', 'true');
        if (this.currentFiltersClosed.caseNumber) {
          this.setUrlParameter('caseNumber', this.currentFiltersClosed.caseNumber);
        }
        if (this.currentFiltersClosed.serialNumber) {
          this.setUrlParameter('serialNumber', this.currentFiltersClosed.serialNumber);
        }
        this.setUrlParameter('order_by', this.currentFiltersClosed.orderBy || 'createdDate');
        if (this.currentFiltersClosed.orderBy) {
          // this.setUrlParameter('order_by', this.currentFiltersClosed.orderBy);
          this.setUrlParameter('direction', 'ASC');
        }
        // return this.paginate(Ticket,1,10)
        return this.paginate(Ticket, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ closed', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ closed error', err)
      }),
      shareReplay(1)
    );

    this.refreshOpened$ = new BehaviorSubject<Pagination>(this.currentOpenedPaginator);
    this.allOpened$ = this.refreshOpened$.pipe(
      switchMap((pagination) => {
        const { locationId } = this.activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        this.setUrlParameter('order_by', 'createdDate');
        this.setUrlParameter('direction', 'DESC');
        this.setUrlParameter('isClosed', 'false');
        return this.paginate(Ticket, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ opened', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ opened error', err)
      }),
      shareReplay(1)
    );
  }

  refreshClosed(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh closed ticket');
    this.currentClosedPaginator = { page, perPage };
    this.refreshClosed$.next(this.currentClosedPaginator);
    return this.allClosed$.pipe(take(1));
  }

  refreshOpened(page: number, perPage: number = PER_PAGE) {
    this.logger.debug(LOG_PREFIX + 'refresh open ticket');
    this.currentOpenedPaginator = { page, perPage };
    this.refreshOpened$.next(this.currentOpenedPaginator);
    return this.allOpened$.pipe(take(1));
  }
}
